import type { BrotliWasmType } from "brotli-wasm";

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const prefix = "BROTLI:";

// Using unpkg because vite doesn't seem to work nicely with brotli-wasm.
// Load on first use: evaluating this module is enough to import the data barrel,
// and Node's ESM loader rejects https: specifiers (vitest then reports an unhandled rejection).
let brotliModule: Promise<BrotliWasmType> | undefined;

const loadBrotli = () => {
  if (!brotliModule) {
    brotliModule = import(
      // @ts-expect-error Doesn't infer types
      "https://unpkg.com/brotli-wasm@3.0.1/index.web.js?module"
    ).then((b) => b.default);
  }
  return brotliModule;
};

export const getSyncBrotli = async () => {
  const brotli = await loadBrotli();
  return {
    compress: <T>(data: T) => compress(brotli, data),
    decompress: <T>(str: string | null | undefined, def: T) => decompress(brotli, str, def),
  };
};

export const brotliDecompress = async <T = unknown>(str: string | null | undefined, def: T) =>
  loadBrotli().then((b) => decompress(b, str, def));

export const brotliCompress = async <T>(data: T) => loadBrotli().then((b) => compress(b, data));

const decompress = <T = unknown>(brotli: BrotliWasmType, str: string | null | undefined, def: T) => {
  if (!str || !str.startsWith(prefix)) return def;
  try {
    const decoded = atob(str.slice(prefix.length));

    const bytes = new Uint8Array(decoded.length);
    for (let i = 0; i < decoded.length; i++) {
      bytes[i] = decoded.charCodeAt(i);
    }

    const jsonStr = decoder.decode(brotli.decompress(bytes));

    return tryParse(jsonStr, def);
  } catch (e) {
    console.error(e);
    return def;
  }
};

const compress = <T>(brotli: BrotliWasmType, data: T) => {
  // Encode JSON string to Uint8Array
  const uncompressedData = encoder.encode(JSON.stringify(data));

  // Compress data
  const compressedDataBytes = brotli.compress(uncompressedData);

  // Convert compressed data to base64 string without using spread operator to save memory
  let binaryString = "";
  for (let i = 0; i < compressedDataBytes.length; i++) {
    binaryString += String.fromCharCode(compressedDataBytes[i]);
  }

  return `${prefix}${btoa(binaryString)}`;
};

export const tryParse = <T>(value: string | null | undefined, def: T): T => {
  if (!value) return def;
  try {
    return JSON.parse(value) as T;
  } catch {
    return def;
  }
};
